import { FC, useMemo, useCallback } from "react";
import classNames from "classnames";
import { Link, Outlet, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { ERoutes } from "../../constants/routes";
import { SITE_URL } from "../../constants/config";
import { selectAuthStepRegister } from "../../store/selectors/auth";
import { setStepRegister } from "../../store/auth/slice";
import { NavItem } from "./types";
import { FOOTER_INFO } from "./const";
import { Button } from "../../components";
import PageTracker from "../../components/PageTracker";

import styles from "./Auth.module.css";



const AuthView: FC = () => {
  const dispatch = useDispatch();
  const step = useSelector(selectAuthStepRegister);
  const location = useLocation();

  const handleStepClick = useCallback((targetStep: number) => {
    if (step > targetStep) {
      dispatch(setStepRegister(targetStep));
    }
  }, [dispatch, step]);

  const mapNavItems = useMemo<NavItem[]>(() => [
    {
      title: "Your Business",
      isCompleted: step > 1,
      isActive: step === 1,
      onClick: () => handleStepClick(1),
    },
    {
      title: "Industry Competitors",
      isCompleted: step > 2,
      isActive: step === 2,
      onClick: () => handleStepClick(2),
    },
    {
      title: "Account Creation",
      isCompleted: step > 3,
      isActive: step === 3,
      onClick: () => handleStepClick(3),
    },
  ], [step, handleStepClick]);

  const renderNavItem = useCallback((item: NavItem) => (
    <li
      key={item.title}
      className={classNames(
        styles["header__nav-item"],
        item.isCompleted && styles["is-completed"],
        item.isActive && styles["is-active"]
      )}
    >
      <Link
        to={ERoutes.Register}
        onClick={item.onClick}
        aria-current={item.isActive ? "page" : undefined}
      >
        {item.title}
      </Link>
    </li>
  ), []);

  return (
    <div className={styles["auth"]}>
      <PageTracker />
      <div className={styles["container"]}>

        <header className={classNames(styles["auth__header"], styles["header"])}>
          <Link to={SITE_URL} className={styles["header__logo"]}>
            <img src="/logo.svg" alt="Logo AICarma" />
          </Link>

          {location.pathname === ERoutes.Register && (
            <nav
              className={styles["header__nav"]}
              aria-label="Registration steps"
            >
              <ul>{mapNavItems.map(renderNavItem)}</ul>
            </nav>
          )}

          <div className={styles["header__actions"]}>
            {location.pathname !== ERoutes.Register && (
              <Link to={ERoutes.Register}>
                <Button typeStyle="fill-dark">Get started</Button>
              </Link>
            )}
          </div>
        </header>

        <div className={styles["auth__main"]}>
          <Outlet />
        </div>

        <footer className={classNames(styles["auth__footer"], styles["footer"])}>
          <p>{FOOTER_INFO.copyright}</p>
          <p>{FOOTER_INFO.address}</p>
          <p className={styles["footer__mail"]}>
            <a href={`mailto:${FOOTER_INFO.email}`}>{FOOTER_INFO.email}</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AuthView;
